import { addCalendarDays, zonedDate, zonedMidnight } from './availability.js'
import { localTimeZone } from './booking-marks.js'

/**
 * The Booking desk's rules (decision 106).
 *
 * **Every job waiting to be booked is in exactly one of five states**, so the
 * counts always add up to the waiting total and nothing can sit unseen:
 *
 * - **Not looked at:** no next date yet. New work, and every imported job.
 * - **Due:** its next date is today or past. Past is late.
 * - **Waiting on the customer:** a link sent, a message left, or no answer,
 *   until its next date.
 * - **Later:** "not until…", until its next date.
 * - **Can't book yet:** on hold, waiting on parts, or a quote not accepted.
 *
 * The desk is clear when Not looked at and Due are both 0.
 *
 * The state is read off three columns on the job (`status`, `followUpAt`,
 * `followUpOn`), so the server can count each state with one indexed query.
 * `deskStateWhere` in the API is this function as SQL; the two are tested
 * against each other.
 */

export const DESK_STATES = ['notLookedAt', 'due', 'waiting', 'later', 'cantBook'] as const
export type DeskState = (typeof DESK_STATES)[number]

export const DESK_STATE_LABEL: Record<DeskState, string> = {
  notLookedAt: 'Not looked at',
  due: 'Due',
  waiting: 'Waiting on the customer',
  later: 'Later',
  cantBook: "Can't book yet",
}

/** The statuses on the waiting list. Matches the API's `waitingWhere`. */
export const DESK_STATUSES = ['APPROVED', 'LEAD', 'QUOTED', 'AWAITING_PARTS', 'ON_HOLD'] as const

/** Waiting, but not bookable until the job itself changes. */
export const CANT_BOOK_STATUSES: readonly string[] = ['QUOTED', 'AWAITING_PARTS', 'ON_HOLD']

export type BookingWaitKind = 'CUSTOMER' | 'LATER'

export interface DeskJob {
  status: string
  followUpAt: Date | null
  followUpOn: BookingWaitKind | null
}

/** The first instant of tomorrow in the account's zone: anything before it is due. */
export function deskDueBefore(now: Date, timeZone: string = localTimeZone()): Date {
  return zonedMidnight(addCalendarDays(zonedDate(now, timeZone), 1), timeZone)
}

export function bookingDeskState(job: DeskJob, now: Date = new Date(), timeZone: string = localTimeZone()): DeskState {
  if (CANT_BOOK_STATUSES.includes(job.status)) return 'cantBook'
  if (!job.followUpAt) return 'notLookedAt'
  if (job.followUpAt < deskDueBefore(now, timeZone)) return 'due'
  return job.followUpOn === 'LATER' ? 'later' : 'waiting'
}

/** Due from before today: drawn red. */
export function deskOverdue(job: DeskJob, now: Date = new Date(), timeZone: string = localTimeZone()): boolean {
  if (!job.followUpAt || CANT_BOOK_STATUSES.includes(job.status)) return false
  return job.followUpAt < zonedMidnight(zonedDate(now, timeZone), timeZone)
}

/**
 * The start of the day `days` working days from now, in the account's zone.
 * "Back in 2 days" from a Friday is Tuesday morning, not Sunday.
 */
export function workingDaysFrom(now: Date, days: number, timeZone: string = localTimeZone()): Date {
  let date = zonedDate(now, timeZone)
  let left = days
  while (left > 0) {
    date = addCalendarDays(date, 1)
    const noon = new Date(zonedMidnight(date, timeZone).getTime() + 12 * 3_600_000)
    const weekday = new Intl.DateTimeFormat('en-AU', { timeZone, weekday: 'short' }).format(noon)
    if (weekday !== 'Sat' && weekday !== 'Sun') left -= 1
  }
  return zonedMidnight(date, timeZone)
}

/* -------------------------------------------------------------------------- */
/* A session's outcomes                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Nothing in a session moves on without one of these. Skip is not an
 * outcome: it sends the job to the end of the session and is counted.
 */
export const DESK_OUTCOMES = ['booked', 'bookAndText', 'sendLink', 'noAnswer', 'notUntil', 'notGoingAhead'] as const
export type DeskOutcome = (typeof DESK_OUTCOMES)[number]

export const DESK_OUTCOME_LABEL: Record<DeskOutcome, string> = {
  booked: 'Booked, they said yes',
  bookAndText: 'Book and text to confirm',
  sendLink: 'Send a pick-a-time link',
  noAnswer: 'No answer',
  notUntil: 'Not until…',
  notGoingAhead: 'Not going ahead',
}

/** How long each kind of wait lasts before the job is due again. */
export const NO_ANSWER_BACK_IN_DAYS = 2
export const LINK_BACK_IN_DAYS = 2
/** A pick-a-time link stops working after this. */
export const OFFER_LIFETIME_DAYS = 14
/** Three tries with no answer, and the session suggests a last-chance email. */
export const LAST_CHANCE_AFTER = 3
/** "Still want this done?": the backlog clear-out (decision 106). */
export const CLEAR_OUT_AGE_DAYS = 12 * 7
export const CLEAR_OUT_CLOSE_AFTER_DAYS = 14
export const NO_RESPONSE_REASON = 'No response'

/** Reasons offered for Not going ahead. Free text is allowed too. */
export const NOT_GOING_AHEAD_REASONS = [
  'Went with someone else',
  'Too expensive',
  'Problem went away',
  'Sold or moved',
  'Duplicate job',
  NO_RESPONSE_REASON,
] as const

export interface AttemptCounts {
  calls: number
  messages: number
  links: number
}

/** "2 calls · 1 link", or null when nobody has tried yet. */
export function attemptsSummary(counts: AttemptCounts): string | null {
  const parts: string[] = []
  if (counts.calls) parts.push(`${counts.calls} ${counts.calls === 1 ? 'call' : 'calls'}`)
  if (counts.messages) parts.push(`${counts.messages} ${counts.messages === 1 ? 'message' : 'messages'}`)
  if (counts.links) parts.push(`${counts.links} ${counts.links === 1 ? 'link' : 'links'}`)
  return parts.length ? parts.join(' · ') : null
}

/** One gap offered on a pick-a-time link, as stored in `BookingOffer.offered`. */
export interface OfferedGap {
  userId: string
  startAt: string
  endAt: string
}
